/**
 * Currency Selector Component
 * Dropdown to select currency with flag icons
 */

import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import { useCurrency, CURRENCIES } from '../context/CurrencyContext';

export default function CurrencySelector({ compact = false }) {
  const { currency, setCurrency, currencyInfo } = useCurrency();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (code) => {
    setCurrency(code);
    setIsOpen(false);
  };

  const currencyList = Object.values(CURRENCIES);

  return (
    <div className="relative" ref={dropdownRef}>
      <motion.button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1.5 px-3 py-2 rounded-xl transition-all"
        style={{
          backgroundColor: isOpen ? 'var(--swoopy-pink-soft)' : 'transparent',
          color: 'var(--text-secondary)'
        }}
        whileTap={{ scale: 0.97 }}
        whileHover={{ backgroundColor: 'var(--swoopy-pink-soft)' }}
        data-testid="currency-selector"
      >
        <span className="text-base">{currencyInfo.flag}</span>
        {!compact && (
          <span className="text-sm font-medium">{currency}</span>
        )}
        <ChevronDown 
          size={14} 
          className={`transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </motion.button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -8, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className="absolute right-0 top-full mt-2 z-50 min-w-[160px]"
            style={{
              backgroundColor: 'white',
              borderRadius: '16px',
              boxShadow: '0 10px 40px -5px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(0, 0, 0, 0.05)',
              overflow: 'hidden'
            }}
          >
            {/* Header */}
            <div 
              className="px-4 py-2.5 text-xs font-semibold uppercase tracking-wider"
              style={{ 
                backgroundColor: 'var(--clay-white)',
                color: 'var(--text-muted)',
                borderBottom: '1px solid var(--border-light)'
              }}
            >
              Mata Uang
            </div>

            {/* Currency Options */}
            <div className="py-1">
              {currencyList.map((curr) => (
                <motion.button
                  key={curr.code}
                  onClick={() => handleSelect(curr.code)}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left transition-colors"
                  style={{
                    backgroundColor: currency === curr.code ? 'var(--swoopy-pink-soft)' : 'transparent',
                    color: currency === curr.code ? 'var(--swoopy-pink)' : 'var(--text-primary)'
                  }}
                  whileHover={{ backgroundColor: 'var(--swoopy-pink-soft)' }}
                  data-testid={`currency-option-${curr.code}`}
                >
                  <span className="text-lg">{curr.flag}</span>
                  <div className="flex-1">
                    <div className="text-sm font-medium">{curr.code}</div>
                    <div className="text-xs" style={{ color: 'var(--text-muted)' }}>
                      {curr.symbol} • {curr.name}
                    </div>
                  </div>
                  {currency === curr.code && (
                    <div 
                      className="w-2 h-2 rounded-full"
                      style={{ backgroundColor: 'var(--swoopy-pink)' }}
                    />
                  )}
                </motion.button>
              ))}
            </div>

            {/* Footer Note */}
            <div 
              className="px-4 py-2.5 text-xs"
              style={{ 
                backgroundColor: 'var(--clay-white)',
                color: 'var(--text-muted)',
                borderTop: '1px solid var(--border-light)'
              }}
            >
              Pembayaran dalam IDR
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
